import { generateSearchTitle } from "../../helper/searchTitle";
import { CompanySearchNotices } from "./CompanySearchNotices";
import { CompanySearchResults } from "./CompanySearchResults";
import CompanySearchShell from "./CompanySearchShell";
import withHomeExecutionReview from "../../components/withHomeExecutionReview";
import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";

import MainLayout from "../../components/layouts/MainLayout";

import { exportCompaniesApi } from "../../api/searchApi";
import { isSearchTimeoutError, searchWithTimeoutRetry } from "./searchWithTimeoutRetry";
import { isMockSearchCompaniesEnabled, MOCK_SEARCH_COMPANIES } from "../../fixture/mockSearchCompanies";

import { FilterControllerProvider } from "./FilterBar/FilterControllerContext";
import { RESULT_VIEW_LIST, RESULT_VIEW_TABLE } from "./FilterBar/ResultViewToggle";
import Defaults from "../../helper/defaults";
import { buildSearchQuery } from "../../helper/searchQuery";
import { areSearchQueriesEquivalent, isSaveSearchDisabled } from "../../helper/savedSearchDirty";
import { mapWorkspaceToSavedSearchOption } from "../Workspaces/savedSearchQuery";
import SaveSearchDialog from "./SaveSearchDialog";

import { createSpreadsheetDraftSessionId } from "../../helper/spreadsheetView";
import useSelectedCompanies from "./useSelectedCompanies";
import { normalizeStoredSearchQuery, readStoredSearchPayload } from "../../helper/storedSearchQuery";

import { buildSearchExportReview, downloadCsvBlob } from "../../helper/csvExport";
import { DEFAULT_SEARCH_PAGE_SIZE } from "./SearchResultsPaginationModel";

import useIsMobile from "../../hooks/useIsMobile";
import useDeferredFilterCommit from "./useDeferredFilterCommit";
import { ALL_COMPANIES_PIN_ID, getDefaultPinnedSavedSearchId } from "../../helper/pinnedSavedSearches";

const FILTER_SEARCH_SKELETON_MIN_MS =
  typeof process !== "undefined" && process.env.JEST_WORKER_ID != null ? 0 : 1500;

/**
 * Horizontal filter toolbar band. Sticky under the fixed logo AppBar so filters
 * remain available while search results scroll. Visuals match the pre-sticky band.
 */

export default withHomeExecutionReview(CompanyList);

function CompanyList() {
  const navigate = useNavigate();
  const { searchKey } = useParams();
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_SEARCH_PAGE_SIZE);
  const [page, setPage] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportReview, setExportReview] = useState(null);
  const [exportError, setExportError] = useState(null);
  const [openSaveSearchDialog, setOpenSaveSearchDialog] = useState(false);
  const [companies, setCompanies] = useState([]);
  const isMobile = useIsMobile();
  const [resultView, setResultView] = useState(RESULT_VIEW_TABLE);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const selectedCompanies = useSelectedCompanies(companies, selectedIds);
  const [spreadsheetDraftSessionHasData, setSpreadsheetDraftSessionHasData] = useState(false);
  const [spreadsheetDraftSessionId, setSpreadsheetDraftSessionId] = useState(createSpreadsheetDraftSessionId);
  const [searchError, setSearchError] = useState(null); // null | 'timeout' | 'generic'
  const [isRetryingSearch, setIsRetryingSearch] = useState(false);
  const [hasSearchCompleted, setHasSearchCompleted] = useState(false);
  // Pagination guarantee: every offered page already has a start cursor, so any
  // interaction costs one search request at most. The total count is informational
  // and never makes an undiscovered page directly clickable.
  const pageStartCursorsRef = useRef([null]);
  const displayedQueryRef = useRef();
  const draftQueryRef = useRef();

  const [locations, setLocations] = useState(Defaults.search.locations);
  const [termsInclude, setTermsInclude] = useState(Defaults.search.termsInclude);
  const [termsExclude, setTermsExclude] = useState(Defaults.search.termsExclude);
  const [industryInclude, setIndustryInclude] = useState(Defaults.search.industryInclude);
  const [industryExclude, setIndustryExclude] = useState(Defaults.search.industryExclude);
  const [revenueLowerBound, setRevenueLowerBound] = useState(Defaults.search.revenueLowerBound);
  const [revenueUpperBound, setRevenueUpperBound] = useState(Defaults.search.revenueUpperBound);
  const [employeesLowerBound, setEmployeesLowerBound] = useState(Defaults.search.employeesLowerBound);
  const [employeesUpperBound, setEmployeesUpperBound] = useState(Defaults.search.employeesUpperBound);
  const [naturalQuery, setNaturalQuery] = useState(Defaults.search.naturalLanguageQuery);
  const restoreDeferredFilters = useCallback((filters) => {
    setTermsInclude(filters.termsInclude); setTermsExclude(filters.termsExclude);
    setIndustryInclude(filters.industryInclude); setIndustryExclude(filters.industryExclude);
    setLocations(filters.locations);
    setRevenueLowerBound(filters.revenueLowerBound); setRevenueUpperBound(filters.revenueUpperBound);
    setEmployeesLowerBound(filters.employeesLowerBound); setEmployeesUpperBound(filters.employeesUpperBound);
  }, []);
  const deferredDraft = {termsInclude, termsExclude, industryInclude, industryExclude, locations, revenueLowerBound, revenueUpperBound, employeesLowerBound, employeesUpperBound};
  const {committedFilters, commitFilters, discardFilters, hydrateFilters, hasUncommittedFilters} =
    useDeferredFilterCommit(deferredDraft, restoreDeferredFilters);
  const [savedSearchesRefreshTrigger, setSavedSearchesRefreshTrigger] = useState(0);
  const [saveSearchSnackbar, setSaveSearchSnackbar] = useState({ open: false, message: "" });
  const [originalSavedFilters, setOriginalSavedFilters] = useState(null);
  const [activeSavedSearchId, setActiveSavedSearchId] = useState(null);
  const [adoptSavedSearch, setAdoptSavedSearch] = useState(null);
  const [isSearchKeyHydrated, setIsSearchKeyHydrated] = useState(false);
  const [isInitialSearchReady, setIsInitialSearchReady] = useState(() => {
    const defaultId = getDefaultPinnedSavedSearchId();
    return !defaultId || defaultId === ALL_COMPANIES_PIN_ID;
  });
  const handleInitialDefaultResolved = useCallback(() => setIsInitialSearchReady(true), []);
  const [searchHydrationVersion, setSearchHydrationVersion] = useState(0);
  const abortControllerRef = useRef(null);
  useEffect(() => {
    const payload = readStoredSearchPayload(searchKey);
    const state = normalizeStoredSearchQuery(payload?.query);
    setTermsInclude(state.termsInclude); setTermsExclude(state.termsExclude);
    setIndustryInclude(state.industryInclude); setIndustryExclude(state.industryExclude);
    setLocations(state.locations);
    setRevenueLowerBound(state.revenueLowerBound); setRevenueUpperBound(state.revenueUpperBound);
    setEmployeesLowerBound(state.employeesLowerBound); setEmployeesUpperBound(state.employeesUpperBound);
    setNaturalQuery(state.naturalLanguageQuery);
    hydrateFilters(state);
    setSelectedIds(new Set());
    setSpreadsheetDraftSessionHasData(false); setSpreadsheetDraftSessionId(createSpreadsheetDraftSessionId());
    draftQueryRef.current = undefined;
    setIsSearchKeyHydrated(true); setSearchHydrationVersion((version) => version + 1);
    setOriginalSavedFilters(null);
    setActiveSavedSearchId(null);

    const hydratedName = typeof payload?.name === "string" ? payload.name.trim() : "";
    if (hydratedName && searchKey) {
      // Show the onboarding (or session) name in the Search banner; filters already applied above.
      setAdoptSavedSearch({
        id: searchKey,
        _id: searchKey,
        name: hydratedName,
        query: payload.query && typeof payload.query === "object" ? payload.query : undefined,
      });
    } else {
      setAdoptSavedSearch(null);
    }
  }, [hydrateFilters, searchKey]);
  useEffect(() => {
    setResultView(isMobile ? RESULT_VIEW_LIST : RESULT_VIEW_TABLE);
  }, [isMobile]);
  // Prefer list cards as the default mobile Search experience.
  useEffect(() => {
    if (isMobile) {
      setResultView(RESULT_VIEW_LIST);
    }
  }, [isMobile]);
  const isStandardFilterEmpty = useMemo(() => (
    committedFilters.locations.length === 0 && committedFilters.termsInclude.length === 0 && committedFilters.termsExclude.length === 0 &&
    committedFilters.industryInclude.length === 0 && committedFilters.industryExclude.length === 0 &&
    (committedFilters.revenueLowerBound === null || committedFilters.revenueLowerBound === Defaults.search.revenueLowerBound) &&
    (committedFilters.revenueUpperBound === null || committedFilters.revenueUpperBound === Defaults.search.revenueUpperBound) &&
    (committedFilters.employeesLowerBound === null || committedFilters.employeesLowerBound === Defaults.search.employeesLowerBound) &&
    (committedFilters.employeesUpperBound === null || committedFilters.employeesUpperBound === Defaults.search.employeesUpperBound)
  ), [committedFilters]);

  const hasNaturalQuery = useMemo(() => naturalQuery && naturalQuery.trim().length > 0, [naturalQuery]);
  const isSearchActive = useMemo(() => !isStandardFilterEmpty || hasNaturalQuery, [isStandardFilterEmpty, hasNaturalQuery]);
  const showResultsEmptyState = useMemo(() => {
    return !loading && (companies?.length || 0) === 0;
  }, [companies?.length, loading]);
  const isSavedSearchDirty = useMemo(() => {
    if (!originalSavedFilters) return false;
    const applied = buildSearchQuery({
      ...committedFilters,
      naturalQuery,
      hasNaturalQuery,
    });
    return !areSearchQueriesEquivalent(originalSavedFilters, applied);
  }, [
    originalSavedFilters,
    committedFilters,
    naturalQuery,
    hasNaturalQuery,
  ]);

  // V3/V4: while a saved search is loaded, Save stays disabled until applied filters diverge.
  const saveSearchDisabled = useMemo(
    () =>
      isSaveSearchDisabled({
        isSearchActive,
        hasSavedBaseline: Boolean(originalSavedFilters),
        isDirty: isSavedSearchDirty,
        requireDirtyWhenSaved: true,
      }),
    [isSavedSearchDirty, isSearchActive, originalSavedFilters]
  );

  const handleSavedSearchBaselineChange = useCallback((query, savedSearch) => {
    setOriginalSavedFilters(query && typeof query === "object" ? query : null);
    setActiveSavedSearchId(query ? String(savedSearch?.id || savedSearch?._id || '') || null : null);
  }, []);

  const handleAdoptSavedSearchConsumed = useCallback(() => {
    setAdoptSavedSearch(null);
  }, []);
  const showingSpreadsheet = resultView === RESULT_VIEW_TABLE;
  const activeQuery = useMemo(() => {
    return buildSearchQuery({
      ...committedFilters,
      naturalQuery,
      hasNaturalQuery,
    });
  }, [committedFilters, naturalQuery, hasNaturalQuery]);
  const activeQueryRef = useRef(activeQuery);
  activeQueryRef.current = activeQuery;
  const activeQueryKey = JSON.stringify(activeQuery);

  const applyMockPage = useCallback((targetPage, pageSize) => {
    const start = targetPage * pageSize;
    setCompanies(MOCK_SEARCH_COMPANIES.slice(start, start + pageSize));
    setTotalRows(MOCK_SEARCH_COMPANIES.length); setHasSearchCompleted(true);
    setPage(targetPage);
  }, []);
  const associateSpreadsheetDraft = useCallback((query) => {
    // Only successful query changes replace a draft; pagination and retries retain paid data.
    if (areSearchQueriesEquivalent(draftQueryRef.current, query)) return;
    draftQueryRef.current = query;
    setSpreadsheetDraftSessionId(createSpreadsheetDraftSessionId());
    setSpreadsheetDraftSessionHasData(false);
  }, []);
  const performSearch = useCallback(async (targetPage = 0, { resetCursors = false } = {}) => {
    if (!isMockSearchCompaniesEnabled()
      && targetPage > 0
      && pageStartCursorsRef.current[targetPage] == null) {
      return;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const startedAt = Date.now();
    let wasCancelled = false;
    const requestQuery = activeQueryRef.current;
    const queryScopeChanged = !areSearchQueriesEquivalent(displayedQueryRef.current, requestQuery);

    setSearchError(null); setIsRetryingSearch(false);
    setLoading(true); setHasSearchCompleted(false);
    setCompanies([]);
    if (resetCursors) {
      pageStartCursorsRef.current = [null];
      setTotalRows(0);
      setPage(0);
    }

    try {
      // Local design mode: slice the hardcoded mock set client-side.
      if (isMockSearchCompaniesEnabled()) {
        associateSpreadsheetDraft(requestQuery);
        applyMockPage(targetPage, rowsPerPage);
      } else {
        if (resetCursors) {
          pageStartCursorsRef.current = [null];
        }

        const cursor = targetPage === 0 ? null : pageStartCursorsRef.current[targetPage];
        const spec = {
          query: requestQuery,
          pagination: {
            page_size: rowsPerPage,
            ...(cursor ? { cursor } : {}),
          },
        };

        const res = await searchWithTimeoutRetry(spec, {
          signal: controller.signal, onRetry: () => setIsRetryingSearch(true),
        });
        if (controller.signal.aborted) return;
        if (res && res.data) {
          const fetchedCompanies = res.data.companies || [];
          associateSpreadsheetDraft(requestQuery);
          setCompanies(fetchedCompanies);
          setTotalRows(res.data.total_count ?? fetchedCompanies.length); setHasSearchCompleted(true);
          setPage(targetPage);
          if (res.data.next_cursor) {
            const nextCursors = [...pageStartCursorsRef.current];
            nextCursors[targetPage + 1] = res.data.next_cursor;
            pageStartCursorsRef.current = nextCursors;
          }
        }
      }
    } catch (error) {
      // A superseded request never reports an error over the newer search.
      if (axios.isCancel(error) || controller.signal.aborted) {
        wasCancelled = true;
        console.log("Search request cancelled");
      } else {
        console.error("Search failed:", error);
        const isSubscriptionRequired = error?.response?.status === 403;
        setSearchError(isSearchTimeoutError(error) ? 'timeout' : isSubscriptionRequired ? 'subscription' : 'generic');
      }
    } finally {
      if (abortControllerRef.current !== controller) {
        return;
      }

      if (!wasCancelled) {
        const remaining = Math.max(0, FILTER_SEARCH_SKELETON_MIN_MS - (Date.now() - startedAt));
        if (remaining > 0) {
          await new Promise((resolve) => setTimeout(resolve, remaining));
        }
      }

      if (abortControllerRef.current === controller) {
        if (!wasCancelled && queryScopeChanged) {
          setSelectedIds(new Set()); displayedQueryRef.current = requestQuery;
        }
        setLoading(false); setIsRetryingSearch(false);
        abortControllerRef.current = null;
      }
    }
  }, [applyMockPage, associateSpreadsheetDraft, rowsPerPage]);

  // Hydration establishes the committed query, including the canonical unfiltered query.
  useEffect(() => {
    if (!isSearchKeyHydrated || !isInitialSearchReady) return undefined;
    pageStartCursorsRef.current = [null];
    setPage(0);
    performSearch(0, { resetCursors: true });
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [activeQueryKey, isInitialSearchReady, isSearchKeyHydrated, performSearch, searchHydrationVersion]);

  const handlePageChange = (nextPage) => {
    if (nextPage === page || nextPage < 0 || loading) return;
    performSearch(nextPage, { resetCursors: false });
  };

  const handleRowsPerPageChange = (nextSize) => {
    if (!nextSize || nextSize === rowsPerPage) return;
    pageStartCursorsRef.current = [null];
    setRowsPerPage(nextSize);
    setPage(0);
  };

  const handleSpreadsheetSelectionChange = (pageSelectedIds, pageRowIds) => {
    if (!pageRowIds || typeof pageRowIds[Symbol.iterator] !== 'function') return;

    setSelectedIds(prev => {
      const next = new Set(prev);
      for (const id of pageRowIds) {
        if (!pageSelectedIds.has(id)) next.delete(id);
      }
      for (const id of pageSelectedIds) next.add(id);
      return next;
    });
  };

  const openExportReview = () => {
    setExportError(null);
    const exportSpec = selectedIds.size ? {company_ids: Array.from(selectedIds)} : {query: activeQuery, limit: 1000};
    if (spreadsheetDraftSessionHasData) exportSpec.draft_session_id = spreadsheetDraftSessionId;
    setExportReview({...buildSearchExportReview({
      selectedCount: selectedIds.size,
      totalRows,
      title: generateSearchTitle(naturalQuery, committedFilters),
    }), exportSpec});
  };

  const exportToCSV = async (filename) => {
    if (isExporting) return;
    setIsExporting(true);
    setExportError(null);
    try {
      const exportSpec = {...exportReview.exportSpec};

      const response = await exportCompaniesApi(exportSpec);

      if (response && response.data) {
        downloadCsvBlob(response.data, filename);
        setExportReview(null);
      } else {
        setExportError("Unable to export these companies. Try again.");
      }
    } catch (err) {
      console.error("Export failed:", err);
      setExportError("Unable to export these companies. Try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleSavedSearchCreated = useCallback((created) => {
    // Stay on Search — refresh catalogs so the new item appears in pickers/list.
    setSavedSearchesRefreshTrigger((prev) => prev + 1);
    setSaveSearchSnackbar({ open: true, message: "Search saved successfully." });

    // Saved configuration becomes the new baseline (original entry is not mutated).
    if (created) {
      const option = mapWorkspaceToSavedSearchOption({
        ...created,
        source_query: created.source_query || created.query || activeQuery,
        query: created.query || created.source_query || activeQuery,
      });
      if (option) {
        setActiveSavedSearchId(String(option.id || option._id));
        setAdoptSavedSearch({
          ...option,
          query: option.query || activeQuery,
        });
        setOriginalSavedFilters(option.query || activeQuery);
        return;
      }
    }

    setActiveSavedSearchId(null);
    setOriginalSavedFilters(activeQuery);
  }, [activeQuery]);

  const showListCards = resultView === RESULT_VIEW_LIST;

  const filterSectionProps = {
    locations,
    setLocations,
    termsInclude,
    setTermsInclude,
    termsExclude,
    setTermsExclude,
    industryInclude,
    setIndustryInclude,
    industryExclude,
    setIndustryExclude,
    revenueLowerBound,
    setRevenueLowerBound,
    revenueUpperBound,
    setRevenueUpperBound,
    employeesLowerBound,
    setEmployeesLowerBound,
    employeesUpperBound,
    setEmployeesUpperBound,
    naturalQuery,
    setNaturalQuery,
    savedSearchesRefreshTrigger,
    isNaturalLanguageMode: hasNaturalQuery,
    // V4: hydrate filters on the Search page (do not navigate to workspace detail).
    savedSearchCatalog: "workspace",
    onSavedSearchBaselineChange: handleSavedSearchBaselineChange,
    adoptSavedSearch,
    onAdoptSavedSearchConsumed: handleAdoptSavedSearchConsumed,
    isSavedSearchDirty,
    savedSearchBaseline: originalSavedFilters,
    onCommitFilters: commitFilters,
    onDiscardFilters: discardFilters,
    onHydrateFilters: hydrateFilters,
    hasUncommittedFilters,
    debounceMultiSelectUpdates: false,
    onInitialDefaultResolved: handleInitialDefaultResolved,
  };

  const searchShell = (
    <CompanySearchResults
    isMobile={isMobile}
    filterSectionProps={filterSectionProps}
    resultView={resultView}
    setResultView={setResultView}
    totalRows={totalRows}
    hasSearchCompleted={hasSearchCompleted}
    openExportReview={openExportReview}
    isExporting={isExporting}
    selectedIds={selectedIds}
    setOpenSaveSearchDialog={setOpenSaveSearchDialog}
    saveSearchDisabled={saveSearchDisabled}
    searchError={searchError}
    isRetryingSearch={isRetryingSearch}
    loading={loading}
    performSearch={performSearch}
    hasNaturalQuery={hasNaturalQuery}
    navigate={navigate}
    showingSpreadsheet={showingSpreadsheet}
    showResultsEmptyState={showResultsEmptyState}
    showListCards={showListCards}
    companies={companies} selectedCompanies={selectedCompanies}
    handleSpreadsheetSelectionChange={handleSpreadsheetSelectionChange}
    spreadsheetDraftSessionId={spreadsheetDraftSessionId}
    setSpreadsheetDraftSessionHasData={setSpreadsheetDraftSessionHasData}
    activeSavedSearchId={activeSavedSearchId}
    isSavedSearchDirty={isSavedSearchDirty}
    page={page}
    rowsPerPage={rowsPerPage}
    pageStartCursorsRef={pageStartCursorsRef}
    handlePageChange={handlePageChange}
    handleRowsPerPageChange={handleRowsPerPageChange}
    setSelectedIds={setSelectedIds}
  />
  );

  return (
    <MainLayout>
      <CompanySearchShell>
        <FilterControllerProvider {...filterSectionProps}>
          {searchShell}
        </FilterControllerProvider>
        <SaveSearchDialog
          open={openSaveSearchDialog}
          onClose={() => setOpenSaveSearchDialog(false)}
          searchSpec={{query: activeQuery, pagination: {page_size: rowsPerPage, cursor: null}}}
          persistMode="workspace"
          onSaveSuccess={handleSavedSearchCreated}
        />
      </CompanySearchShell>
      <CompanySearchNotices
        exportReview={exportReview}
        isExporting={isExporting}
        exportError={exportError}
        setExportReview={setExportReview}
        setExportError={setExportError}
        exportToCSV={exportToCSV}
        saveSearchSnackbar={saveSearchSnackbar}
        setSaveSearchSnackbar={setSaveSearchSnackbar}
      />
    </MainLayout>
  );
}
